import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { MapContext } from '@/feature/map/context/map'

/**
 * Owns the `google.maps.TrafficLayer` instance behind the traffic toggle.
 *
 * The toggle is usable with or without a caller controlling it, so the on/off state is held locally
 * and re-synced whenever a controlled `value` changes.
 */
export const useTrafficLayer = (value: boolean, onValueChange: (value: boolean) => void) => {
  const [traffic, setTraffic] = useState(value)

  const map = useContext(MapContext)

  const trafficLayer = useMemo(() => new google.maps.TrafficLayer(), [])

  useEffect(() => {
    setTraffic(value)
  }, [value])

  useEffect(() => {
    trafficLayer.setMap(traffic ? (map?.map ?? null) : null)
  }, [traffic, map?.map, trafficLayer])

  // Detaching is unconditional: the map arrives from context after mount, so a cleanup that only
  // ran when a map was already in scope would close over the initial empty context and leave the
  // layer attached for the lifetime of the map.
  useEffect(() => {
    return () => {
      trafficLayer.setMap(null)
    }
  }, [trafficLayer])

  const toggleTraffic = useCallback(
    (next: boolean) => {
      onValueChange(next)
      setTraffic(next)
    },
    [onValueChange],
  )

  return { traffic, toggleTraffic }
}
